/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { Spring } from 'react-spring';

import badges from 'assets/images/badges';

import BadgeDisplay from 'components/BadgeDisplay';
import IntroChapterPopup from 'components/IntroChapterPopup';
import Progress from 'components/Progress';
import ItineraryModuleCard from 'components/TrainingGrid/ItineraryModuleCard';
import TrainingCard from 'components/TrainingGrid/TrainingCard';
import TrainingCategoryCard from 'components/TrainingGrid/TrainingCategoryCard';
import TrainingExtraCard from 'components/TrainingGrid/TrainingExtraCard';
import TrainingGridAccordion from 'components/TrainingGrid/TrainingGridAccordion';

import APIHandler from 'config/APIHandler';

import { getExternalAppChapterUrl } from 'helpers/externalAppUrl';

import { useChapters } from 'hooks/useChapters';
import { usePointsHandler } from 'hooks/usePointsHandler';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const SubProgressBar = ({ type = null, progress }) => {
  const roundedProgress = Math.round(progress);

  return (
    <div className={`subprogress ${type}`}>
      <Progress id="subprogress" value={roundedProgress} rounded />
      <p>
        {progress} {i18n.translate('trainingScreens.percentageComplete')}
      </p>
    </div>
  );
};

SubProgressBar.propTypes = {
  progress: PropTypes.number.isRequired,
  type: PropTypes.oneOf([null, 'bronze', 'silver', 'gold', 'platinum']),
};

const TrainingGrid = () => {
  const {
    appId,
    lang: { code, langFile },
  } = useSettings();
  const { user: { email }, updateUser } = useUserInfo();
  const {
    chapters,
    categories,
    groups,
    subapps,
    itineraries,
    groups: { main, sub },
    loaded: chaptersLoaded,
  } = useChapters();

  // console.log('trainingGrid - useChaptersData:', {
  //   chapters,
  //   categories,
  //   subapps,
  //   itineraries,
  //   groups,
  //   main,
  //   sub,
  //   loaded: chaptersLoaded,
  // })

  // calculates the diploma progress rate based on the pages viewed across the chapters in the main group.
  const diplomaProgressRate = useMemo(() => {
    const {
      main: { chapterIDs },
    } = groups;

    const { pagesViewed, totalPages, allComplete } = chapters.reduce(
      (acc, chapter) => {
        const { id, percentage, progress, chapterItemCount } = chapter;
        if (chapterIDs.includes(id)) {
          const chapterPages = Math.max((Number(chapterItemCount) || 0) - 1, 0);
          // completed chapters count as fully viewed; otherwise cap progress at the chapter's page count
          const chapterViewed = percentage === 100 ? chapterPages : Math.min(Number(progress) || 0, chapterPages);
          acc.totalPages += chapterPages;
          acc.pagesViewed += chapterViewed;
          if (percentage !== 100) acc.allComplete = false;
        }
        return acc;
      },
      { pagesViewed: 0, totalPages: 0, allComplete: true },
    );

    if (totalPages === 0) return 0;

    const rate = Math.floor((pagesViewed / totalPages) * 100);

    // only show 100% (and the "completed" state) once every chapter is actually complete
    return allComplete ? 100 : Math.min(rate, 99);
  }, [chapters, groups]);

  const { tiers, totalPoints, getTieredValue, setPoints, loaded: pointsHandlerLoaded } = usePointsHandler();
  const tierIndex = tiers && Math.min(getTieredValue(), tiers.length - 1);

  const isDataReady = useMemo(
    () =>
      pointsHandlerLoaded &&
      chapters.length > 0 &&
      categories.length > 0 &&
      subapps.length > 0 &&
      itineraries.length > 0,
    [pointsHandlerLoaded, chapters, categories, subapps, itineraries],
  );

  /**
   * Handles the point updates for chapters, subapps, and itineraries. Four behaviors:
   *
   * 1. Foundational chapters: For single, independant chapters, the points are to be updated for each completed chapter
   *    with the action: `intro-chapter` and value: `intro-chapter-${chapter.id}.
   * 2. Grouped catogories (e.g. Big City Buzz), the points are to be updated ONCE for each fully completed collection of
   *    chapters with the action: `specialization` and value: `specialization-${category.id}`
   * 3. Grouped subapps (e.g. partner modules), the points are to be updated for each individual completed module with the
   *    action: `subapp` and value: `subapp-${sessionAppId}`
   * 4. Itinerary / specialization modules (`by_subapps_specializations`): same `subapp` / `subapp-${sessionAppId}` contract
   *    as (3) when percentage === 100.
   */
  useEffect(() => {
    const handlePointUpdates = async () => {
      // Gather the foundational chapters
      const { foundationalChapters } = chapters.reduce(
        (acc, chapter) => {
          const { id, percentage } = chapter;
          // If the chapter is in the main group and is completed, add it to the foundational chapters
          if (main.chapterIDs.includes(id) && percentage === 100) {
            acc.foundationalChapters.push(chapter);
            // if the chapter is in the sub group and is completed, add it to the specialist chapters
          }
          return acc;
        },
        { foundationalChapters: [] },
      );

      const { experienceCategories } = categories.reduce(
        (acc, category) => {
          const { id } = category;
          if (sub.categoryIDs.includes(id)) {
            acc.experienceCategories.push(category);
          }
          return acc;
        },
        { experienceCategories: [] },
      );

      // Since updates are done on a per-module basis, we need to flatten the subapps array to get all the modules.
      const subappModules = subapps.flatMap(({ modules }) => modules);

      // Same for itineraries (specializations API): one flat list of modules per category.
      const itineraryModules = (itineraries || []).flatMap((category) => category.modules || []);

      const foundationalUpdates = foundationalChapters
        .filter(({ percentage, id }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(id);
          return isComplete && hasValidId;
        })
        .map(({ id }) => ({ action: 'intro-chapter', value: `intro-chapter-${id}` }));

      const experienceUpdates = experienceCategories
        .filter(({ percentage, id }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(id);
          return isComplete && hasValidId;
        })
        .map(({ id }) => ({ action: 'specialization', value: `specialization-${id}` }));

      const subappUpdates = subappModules
        .filter(({ percentage, sessionAppId }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(sessionAppId);
          return isComplete && hasValidId;
        })
        .map(({ sessionAppId }) => ({ action: 'subapp', value: `subapp-${sessionAppId}` }));

      const itineraryUpdates = itineraryModules
        .filter(({ percentage, sessionAppId }) => {
          const isComplete = percentage === 100;
          const hasValidId = !Number.isNaN(sessionAppId);
          return isComplete && hasValidId;
        })
        .map(({ sessionAppId }) => ({ action: 'itinerary-badge', value: `itinerary-badge-${sessionAppId}` }));

      const allUpdates = [
        ...foundationalUpdates,
        ...experienceUpdates,
        ...subappUpdates,
        ...itineraryUpdates,
      ];

      await setPoints(allUpdates);
    };
    if (isDataReady) {
      handlePointUpdates();
    }
  }, [categories, chapters, isDataReady, itineraries, main.chapterIDs, setPoints, sub.categoryIDs, subapps]);

  // takes a list of chapters and returns the total number of chapters and the number of completed chapters.
  const getItemsFromChapters = (chapterList) =>
    chapterList.reduce(
      (acc, chapter) => {
        const { percentage } = chapter;
        acc.totalItemCount += 1;
        if (percentage === 100) acc.totalItemCountCompleted += 1;
        return acc;
      },
      { totalItemCountCompleted: 0, totalItemCount: 0 },
    );

  const isLockedChapter = useCallback(
    (id) => {
      // if chapters aren't loaded, this means that the id is locked by default.
      // eslint-disable-next-line no-unreachable
      if (!chapters || chapters.length === 0) return true;

      // if chapters aren't found, this means that chapters are locked by default.
      const relevantChapter = chapters.find(({ id: chapterId }) => chapterId === id);
      if (!relevantChapter) return true;

      const { chapterNumber, chapterItemCount: totalPages } = relevantChapter;

      // chapters without pages are locked
      if (totalPages === 0) return true;

      // if the chapter is the first chapter, it should be unlocked.
      if (chapterNumber === 1 || chapterNumber === 2) return false;

      const previousChapter = chapters.find(
        ({ chapterNumber: prevChapterNumber }) => Number(prevChapterNumber) === Number(chapterNumber) - 1,
      );

      // if the previous chapter is completed (percentage === 100), the chapter should be unlocked.
      const { percentage: previousChapterPercentage } = previousChapter || {};
      return previousChapterPercentage !== 100;
    },
    [chapters],
  );

  // calculates the completion rate of a category based on the chapters in the category
  const calculateGroupCompletionRate = (group) => {
    const { chapterIDs = [] } = group || {};
    const groupChapters = chapters.filter(({ id }) => chapterIDs.includes(id));

    const { totalItemCountCompleted, totalItemCount } = getItemsFromChapters(groupChapters);

    if (totalItemCount === 0) return 0;

    return Math.round((totalItemCountCompleted / totalItemCount) * 100);
  };

  // const { totalItemCountCompleted } = useMemo(() => getItemsFromChapters(chapters), [chapters]);

  const foundationalCompletionRate = calculateGroupCompletionRate(main);
  const specialistCompletionRate = calculateGroupCompletionRate(sub);
  const extraCompletionRate = calculateGroupCompletionRate(subapps);

  const { tierBadges } = badges;

  const progressVideo =useCallback(async (chapter) => {
    if (chapter.video){

      const updateInfo = {
        chapterId: chapter.id,
        progress: chapter.chapterItemCount,
        lastPage: chapter.chapterItemCount,
        email,
      };

      const d = new Date(Date.now());
      const leading = (val) => `0${val}`.slice(-2);
      const date = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(d.getDate())}`;


      await APIHandler.setUserProgress(updateInfo);
      await APIHandler.setChapterCompleted(appId, email, chapter.id, chapter.chapterNumber);
      await updateUser(appId, [
        { param: 'training_started', val: date },
        { param: 'sales_companion_registered', val: date },
      ]);
    }
  }, [appId, email]);

  const renderChapterLink = useCallback(
    (chapter, className, locked = false) => {
      const classes = clsx('trainingCard__Animated', className);
      const locale = `${langFile}-${langFile.toUpperCase()}`;

      const { id, video } = chapter;

      let videoUrl = video;
      if (locale !== 'en-EN' && video && video.includes('.mp4')) {
        // Replace .mp4 with _{locale}.mp4
        videoUrl = video.replace(/.mp4$/, `_${locale}.mp4`);
      }

      return (
        <div 
          className={classes} 
          key={`ch${id}`} 
          id={`ch${id}`} 
          data-fancybox={videoUrl} 
          data-src={videoUrl} 
          onClick={() => progressVideo(chapter)} 
        >
          <TrainingCard appId={appId} chapter={chapter} redirPath="/main/training/chapter/" locked={locked} />
        </div>
      );
    },
    [appId, langFile, progressVideo],
  );

  const renderExtraCategoryLink = useCallback(
    (module, className, locked = false, linkPathSegment = 'subapps') => {
      const classes = clsx('trainingCard__Animated', className);
      const { id } = module;
      const redirPath =
        linkPathSegment === 'itineraries' ? '/main/training/itineraries/' : '/main/training/subapps/';
      return (
        <div className={classes} key={`ex${id}`}>
          <TrainingExtraCard
            appId={appId}
            module={module}
            redirPath={redirPath}
            linkPathSegment={linkPathSegment}
            locked={locked}
            foundationLocked={foundationalCompletionRate < 100}
          />
        </div>
      );
    },
    [appId, foundationalCompletionRate],
  );

  // render function for an entire category. (to subpage)
  const renderCategoryLink = useCallback(
    (category, className, locked = false) => {
      const classes = clsx('trainingCard__Animated', className);

      const { id, chapterIDs } = category;
      const categoryChapters = chapters.filter(({ id: chapterID }) => chapterIDs.includes(chapterID));
      return (
        <div className={classes} key={`ca${id}`}>
          <TrainingCategoryCard
            appId={appId}
            category={category}
            chapters={categoryChapters}
            redirPath="/main/training/extra"
            locked={locked}
            foundationLocked={foundationalCompletionRate < 100}
          />
        </div>
      );
    },
    [appId, chapters, foundationalCompletionRate],
  );

  // render function for a group. Checks if the group should be rendered as a whole category, or if it should render the chapters individually. style is added to allow for styling.
  const renderGroup = useCallback(
    (group, style) => {
      const { categoryIDs = [] } = group || {};
      const groupCategories = categories.filter(({ id }) => categoryIDs.includes(id));

      const links = groupCategories.flatMap((category) => {
        const { openableSection, chapterIDs } = category;

        // openableSection is a boolean that determines if the category is 'openable', meaning it should be render the category as a whole. Else, it should render the chapters directly.
        if (openableSection) {
          const totalNumberOfPages = chapters
            .filter(({ id }) => chapterIDs.includes(id))
            .reduce((acc, chapter) => {
              const { chapterItemCount } = chapter;
              return acc + Number(chapterItemCount);
            }, 0);
          const locked = totalNumberOfPages === 0 || foundationalCompletionRate < 100;
          return renderCategoryLink(category, clsx('specialization', `trainingGrid__training-card--${style}`), locked);
        }

        return chapters
          .filter((chapter) => {
            const { id } = chapter;
            return chapterIDs.includes(id);
          })
          .map((chapter) => {
            const { id } = chapter;
            const locked = isLockedChapter(id);
            return renderChapterLink(chapter, `trainingGrid__training-card--${style}`, locked);
          });
      });
      return links;
    },
    [categories, chapters, isLockedChapter, renderCategoryLink, renderChapterLink, foundationalCompletionRate],
  );

  // render function for the extra learning category. (to subpage)
  const renderSubappsLinks = useCallback(
    (style) => {
      const links = subapps.map((module) => {
        const { chapterItemCount: totalPages } = module || {};
        const locked = totalPages === 0 || foundationalCompletionRate < 100; // lock if there are no pages within the chapter
        return renderExtraCategoryLink(module, `trainingGrid__training-card--${style}`, locked);
      });
      return links;
    },
    [renderExtraCategoryLink, subapps, foundationalCompletionRate],
  );

  const renderItineraryLinks = useCallback(
    (style) => {
      const styleClass = `trainingGrid__training-card--${style}`;
      return (itineraries || []).flatMap((category) => {
        const openableSection = category.openableSection !== false;
        if (openableSection) {
          const { chapterItemCount: totalPages } = category || {};
          const locked = totalPages === 0 || foundationalCompletionRate < 100;
          return [renderExtraCategoryLink(category, styleClass, locked, 'itineraries')];
        }
        const { modules = [] } = category;
        return modules.map((module) => (
          <ItineraryModuleCard
            key={module.id}
            appId={appId}
            module={module}
            redirPath={getExternalAppChapterUrl({
              subAppId: (module.sub_app && module.sub_app.id) || module.sessionAppId || '',
              chapterId: module.session_chapter_id != null ? module.session_chapter_id : 0,
              totalCount: (module.count && module.count.total_count) != null
                ? module.count.total_count : 0,
              highestPage: (module.progress && module.progress.highest_page) != null
                ? module.progress.highest_page : 0,
              email,
              langFile,
            })}
            linkPathSegment="itineraries"
            className={styleClass}
            foundationLocked={foundationalCompletionRate < 100}
            locked={foundationalCompletionRate < 100}
          />
        ));
      });
    },
    [itineraries, renderExtraCategoryLink, foundationalCompletionRate, appId, email, langFile],
  );

  const foundationalLinks = useMemo(() => renderGroup(main, 'main'), [main, renderGroup]);
  const specialistLinks = useMemo(() => renderGroup(sub, 'sub'), [renderGroup, sub]);
  const extraLearningLinks = useMemo(() => renderSubappsLinks('extra'), [renderSubappsLinks]);
  const itineraryLinks = useMemo(() => renderItineraryLinks('extra'), [renderItineraryLinks]);

  // opens the accordion panels based on the completion rate of the chapters
  const opened = useMemo(() => {
    const requiredProgress = 95;
    const base = [];

    if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate < requiredProgress &&
      extraCompletionRate < requiredProgress
    ) {
      base.push('specialized', 'extra', 'itineraries');
    } else if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate >= requiredProgress &&
      extraCompletionRate >= requiredProgress
    ) {
      base.push('foundational', 'specialized', 'extra', 'itineraries');
    } else if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate >= requiredProgress &&
      extraCompletionRate < requiredProgress
    ) {
      base.push('extra', 'itineraries');
    } else if (foundationalCompletionRate >= requiredProgress && specialistCompletionRate < requiredProgress) {
      base.push('specialized');
    } else {
      base.push('foundational');
    }
    return base;
  }, [extraCompletionRate, foundationalCompletionRate, specialistCompletionRate]);

  return (
    <Spring from={{ transform: 'translateY(30%)', opacity: 0 }} to={{ transform: 'translateY(0%)', opacity: 1 }}>
      {() => (
        <React.Fragment>
          {pointsHandlerLoaded && (
            <BadgeDisplay
              tierBadges={tierBadges}
              tierIndex={tierIndex}
              tiers={tiers}
              totalPoints={totalPoints}
              layout="pillar"
              position="right"
            />
          )}
          {chaptersLoaded && (
            <React.Fragment>
              <IntroChapterPopup chapters={chapters} />
              <TrainingGridAccordion
                diplomaProgressRate={diplomaProgressRate}
                opened={opened}
                foundationalLinks={foundationalLinks}
                specialistLinks={specialistLinks}
                itineraryLinks={itineraryLinks}
                extraLearningLinks={extraLearningLinks}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Spring>
  );
};

export default TrainingGrid;
